/**
 * GainEnvelope — единственный источник правды по вопросу «какое усиление
 * применяется в момент t».
 *
 * Зачем это существует
 * ────────────────────
 * У /volume этот вопрос был описан ПЯТЬ раз на пяти разных языках: JS-функция
 * `getGainAt()` для превью, массив объектов для AudioStreamPipeline, другой
 * массив объектов для MediaPipeline, строка FFmpeg-фильтров в блейде и ещё одна
 * строка FFmpeg-фильтров в VolumeHandler.php. Пять реализаций не могут не
 * разъезжаться, и они разъехались — замеры 2026-08-21 на проде:
 *
 *   • граница участка в экспорте опаздывала на 48 мс (`volume=enable=between`
 *     включается ПО КАДРАМ, а не по сэмплам — проверено и на `eval=frame`,
 *     результат тот же до микросекунды);
 *   • на границе стоял разрыв — excess sample-step до 92.85× собственного шага
 *     сигнала на 40 Гц (на 4 кГц всего 1.13×, поэтому «щелчок» на самом деле
 *     низкочастотный удар и на речи слышен хуже, чем на музыке);
 *   • превью считало усиление сэмплово, экспорт — по кадрам, то есть человек
 *     слышал одно, а скачивал другое;
 *   • пересекающиеся участки: превью брало ПЕРВЫЙ (`break`), экспорт
 *     ПЕРЕМНОЖАЛ все — два участка по +6 дБ давали в файле +12 дБ.
 *
 * Контракт
 * ────────
 * Огибающая — это список опорных точек `[{t, g}]`, отсортированный по времени,
 * с ЛИНЕЙНОЙ интерполяцией в ЛИНЕЙНОМ домене усиления между соседними точками
 * и удержанием крайних значений за пределами списка.
 *
 * Домен выбран не произвольно: ровно так же ведёт себя
 * `AudioParam.linearRampToValueAtTime`, поэтому веб-аудиный рендерер получается
 * не «похожим», а точным — он исполняет контракт нативно, в аудиопотоке.
 *
 * Рендереры (все обязаны совпадать — их держит `tests/fixtures/gain-envelope-vectors.json`)
 * ────────────────────────────────────────────────────────────────────────────
 *   valueAt(t)                       → отрисовка волны
 *   scheduleOn(param, when, offset)  → превью (живой ctx) И lite-лейн (offline ctx)
 *   applyToChannels(...)             → стриминг-лейн, по чанкам, O(chunk) памяти
 *   toFFmpegExpr(nChannels)          → WASM-лейн и сервер (PHP повторяет ту же формулу)
 *
 * Добавляя новый лейн, реализуй контракт и прогони те же векторы. Именно это
 * место должно оставаться единственным, где живёт слово «усиление».
 */
(function (root) {
    'use strict';

    /**
     * Длина рампы на изломе огибающей, миллисекунды.
     *
     * Метрика — excess step: максимальный скачок между соседними сэмплами
     * рядом с границей, делённый на СОБСТВЕННЫЙ максимальный шаг того же
     * сигнала при ТОМ ЖЕ усилении внутри участка. 1.00 = переход неотличим от
     * материала. Замер 2026-08-21, худшее по 24 фазам границы, бас 40 Гц
     * (на 1 кГц жёсткая ступень даёт всего 2.10 — «щелчок» на самом деле
     * низкочастотный удар, и на музыке он слышен куда лучше, чем на речи):
     *
     *   рампа   +3 дБ   +6 дБ  +12 дБ  +20 дБ
     *   жёстко  27.89   47.63   71.51   85.94   ← как было до 2026-08-21
     *   1 мс     1.53    2.22    3.13    3.68
     *   2 мс     1.16    1.41    1.79    2.05
     *   4 мс     1.04    1.11    1.24    1.34
     *   8 мс     1.01    1.03    1.06    1.09   ← выбрано
     *   16 мс    1.00    1.01    1.02    1.02
     *
     * 8 мс — точка, где даже +20 дБ на басу даёт 1.09, то есть переход
     * практически не выделяется на фоне самого материала. 16 мс добавляет
     * почти ничего и вдвое сильнее размазывает границу; 2 мс (первый выбор в
     * этой сессии) держится только на умеренных усилениях.
     *
     * Форма рампы — ЛИНЕЙНАЯ, и это тоже замер, а не вкус: приподнятый косинус
     * при той же длине ХУЖЕ (2 мс: 1.74 против 1.41; 1 мс: 3.20 против 2.22),
     * потому что его максимальная крутизна в середине выше средней. Линейная
     * рампа заодно — ровно то, что `AudioParam.linearRampToValueAtTime` делает
     * нативно, так что браузерный рендерер исполняет контракт без приближений.
     *
     * Менять это число можно только с новым замером — проба лежит в
     * tests/node/gain-envelope-conformance.mjs (случай `no_click_at_40hz`).
     */
    var DEFAULT_RAMP_MS = 8;

    /** Сколько точек вставлять там, где ОБА множителя (участки и fade) меняются
     *  одновременно: их произведение там квадратично, а контракт линеен. Окно
     *  такого наложения ≤ длины рампы (2 мс), поэтому 8 отрезков дают ошибку
     *  много меньше слышимой — фактическая величина проверяется вектором
     *  `fade_over_region_ramp`. */
    var QUAD_SUBDIV = 8;

    /**
     * Минимальная ширина излома, секунды (один сэмпл на 48 кГц).
     *
     * Контракт — кусочно-ЛИНЕЙНАЯ функция, и две точки с одинаковым временем в
     * ней неоднозначны: интерполяция не может вернуть два значения сразу.
     * Пойман замером 2026-08-21: при `rampMs:0` пара точек в одном t
     * схлопывалась дедупликацией, и «жёсткая ступень» молча превращалась в
     * ДВУХСЕКУНДНЫЙ плавный подъём — excess step 1.02 вместо ожидаемых
     * десятков. Сторож, который должен был доказывать пользу рампы, доказывал
     * бы её сравнением с несуществующим поведением.
     *
     * Поэтому `build()` никогда не порождает нулевую ширину: вырожденный излом
     * получает один сэмпл. На 40 Гц это неотличимо от настоящей ступени
     * (проверяется вектором `hard_step_legacy`), но представимо в контракте.
     */
    var MIN_RAMP_SEC = 1 / 48000;

    var EPS = 1e-9;

    function dbToLin(db) { return Math.pow(10, db / 20); }
    function num(v, dflt) { var n = Number(v); return isFinite(n) ? n : dflt; }
    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

    /**
     * Число в FFmpeg-выражении — ЧАСТЬ кросс-языкового контракта, а не деталь
     * печати. Формат задан явно: фиксированная точка, 9 знаков, хвостовые нули
     * срезаны.
     *
     * Замерено 2026-08-21: пока формат наследовался от родного `String()`,
     * JS печатал `0.000020834`, а PHP на том же числе — `2.0834e-5`. Оба
     * значения верные, строки разные, и сторож совпадения языков падал на
     * форматировании вместо математики: шумел бы там, где всё в порядке, и
     * замолчал бы, спрячься настоящая разница в тех же знаках.
     *
     * 9 знаков покрывают и время суток (86400 с), и самый узкий излом (~2e-5 с).
     */
    var NUM_DECIMALS = 9;
    var NUM_EPS = 1e-9;
    function ffNum(v) {
        var s = v.toFixed(NUM_DECIMALS);
        if (s.indexOf('.') >= 0) s = s.replace(/0+$/, '').replace(/\.$/, '');
        return s === '-0' ? '0' : s;
    }

    /* ───────────────────────── piecewise-linear helpers ───────────────────── */

    /** Значение кусочно-линейной функции, заданной точками, в момент t. */
    function interpAt(pts, t) {
        var n = pts.length;
        if (!n) return 1;
        if (t <= pts[0].t) return pts[0].g;
        if (t >= pts[n - 1].t) return pts[n - 1].g;
        var lo = 0, hi = n - 1;
        while (hi - lo > 1) {
            var mid = (lo + hi) >> 1;
            if (pts[mid].t <= t) lo = mid; else hi = mid;
        }
        var a = pts[lo], b = pts[hi];
        var span = b.t - a.t;
        if (span <= EPS) return b.g;
        return a.g + (b.g - a.g) * ((t - a.t) / span);
    }

    /** Меняется ли функция на отрезке [ta, tb] (нужно, чтобы понять, где
     *  произведение двух множителей перестаёт быть линейным). */
    function variesOver(pts, ta, tb) {
        return Math.abs(interpAt(pts, ta) - interpAt(pts, tb)) > EPS;
    }

    /* ───────────────────────────── regions ────────────────────────────────── */

    /**
     * Пересекающиеся участки → непересекающееся разбиение, ПОЗДНИЙ ПОБЕЖДАЕТ.
     *
     * Почему поздний, а не сумма: человек, выделивший кусок второй раз и
     * поставивший другое число, меняет решение, а не добавляет к прежнему.
     * Сумма (то, что делал экспорт) удивляет: два раза по +6 дБ давали +12.
     * Первый-побеждает (то, что делало превью) удивляет ещё сильнее — свежая
     * правка молча не применяется.
     */
    function resolveRegions(regions, t0, t1, defaultRamp) {
        defaultRamp = Math.max(0, num(defaultRamp, DEFAULT_RAMP_MS / 1000));
        var list = [], i, j;
        var src = regions || [];
        for (i = 0; i < src.length; i++) {
            var r = src[i];
            if (!r) continue;
            var s = clamp(num(r.start, NaN), t0, t1);
            var e = clamp(num(r.end, NaN), t0, t1);
            if (!isFinite(s) || !isFinite(e) || !(e > s + EPS)) continue;
            list.push({
                start: s, end: e, lin: dbToLin(num(r.gain, 0)),
                // Своя длина перехода у КАЖДОГО участка. Умолчание —
                // антищелчковый минимум; больше делает из участка плавный
                // наплыв, чего прямоугольником не выразить.
                fade: Math.max(0, num(r.fade, defaultRamp)),
            });
        }
        if (!list.length) return [{ start: t0, end: t1, lin: 1, fade: defaultRamp }];

        var edges = [t0, t1];
        for (i = 0; i < list.length; i++) edges.push(list[i].start, list[i].end);
        edges.sort(function (a, b) { return a - b; });

        var out = [];
        for (i = 0; i + 1 < edges.length; i++) {
            var a = edges[i], b = edges[i + 1];
            if (!(b > a + EPS)) continue;
            var mid = (a + b) / 2, lin = 1, fade = defaultRamp;
            for (j = 0; j < list.length; j++) {
                if (mid > list[j].start - EPS && mid < list[j].end + EPS) { lin = list[j].lin; fade = list[j].fade; }
            }
            var prev = out.length ? out[out.length - 1] : null;
            if (prev && Math.abs(prev.lin - lin) < EPS && Math.abs(prev.end - a) < EPS) {
                prev.end = b;
                // Слитые равные плато наследуют более плавный переход: иначе
                // соседство молча укоротило бы запрошенный наплыв.
                prev.fade = Math.max(prev.fade, fade);
            } else out.push({ start: a, end: b, lin: lin, fade: fade });
        }
        return out.length ? out : [{ start: t0, end: t1, lin: 1, fade: defaultRamp }];
    }

    /**
     * Разбиение на участки → кусочно-линейные точки с рампами на изломах.
     *
     * Рампа СИММЕТРИЧНА относительно границы (±half), поэтому середина рампы —
     * ровно запрошенное время: слышимая граница не съезжает, в отличие от
     * односторонней рампы, которая опаздывает на всю свою длину.
     *
     * Рампа ужимается, если соседний участок короче неё: два излома не должны
     * налезать друг на друга, иначе огибающая перестаёт достигать заданного
     * значения и «+6 дБ» на коротком участке молча превращается в +4.
     *
     * На краях окна (t0/t1) рампы НЕТ намеренно: там нет предыдущего звука, с
     * которым можно было бы получить разрыв.
     */
    function regionPoints(segs) {
        var pts = [{ t: segs[0].start, g: segs[0].lin }];
        for (var k = 1; k < segs.length; k++) {
            var prev = segs[k - 1], cur = segs[k];
            if (Math.abs(cur.lin - prev.lin) < EPS) continue;
            // Переход берёт БОЛЬШУЮ длину из двух соседей: если у одного из
            // них попросили плавный наплыв, соседство не должно его отменять.
            var half = Math.max(prev.fade, cur.fade) / 2;
            var h = Math.max(
                MIN_RAMP_SEC / 2,
                Math.min(half, (prev.end - prev.start) / 2, (cur.end - cur.start) / 2)
            );
            var tau = cur.start;
            pts.push({ t: tau - h, g: prev.lin });
            pts.push({ t: tau + h, g: cur.lin });
        }
        var last = segs[segs.length - 1];
        pts.push({ t: last.end, g: last.lin });
        return pts;
    }

    /* ────────────────────────────── fades ─────────────────────────────────── */

    function fadePoints(t0, t1, fadeInSec, fadeOutSec) {
        var dur = t1 - t0;
        if (dur <= EPS) return [{ t: t0, g: 1 }, { t: t1, g: 1 }];
        var fin = clamp(num(fadeInSec, 0), 0, dur);
        var fout = clamp(num(fadeOutSec, 0), 0, dur);
        // Наложение fade-in и fade-out на коротком файле: ужимаем оба, чтобы
        // они встретились ровно посередине, а не перемножились в провал.
        if (fin + fout > dur) {
            var scale = dur / (fin + fout);
            fin *= scale; fout *= scale;
        }
        var pts = [];
        if (fin > EPS) { pts.push({ t: t0, g: 0 }); pts.push({ t: t0 + fin, g: 1 }); }
        else pts.push({ t: t0, g: 1 });
        if (fout > EPS) { pts.push({ t: t1 - fout, g: 1 }); pts.push({ t: t1, g: 0 }); }
        else pts.push({ t: t1, g: 1 });
        return pts;
    }

    /* ─────────────────────────── restore (de-fade) ─────────────────────────── */

    /** Точки множителя «вернуть громкость» — кусочно-линейные в линейном
     *  усилении, как и весь контракт; за краями удерживаются крайние значения
     *  (1 до затухания, 0 после новой концовки). Мусор → нейтральный множитель:
     *  молча исказить звук хуже, чем не вернуть громкость. */
    function restorePointsOf(list) {
        if (!list || !list.length) return [{ t: 0, g: 1 }];
        var out = [];
        for (var i = 0; i < list.length; i++) {
            var p = list[i];
            var t = num(p && p.t, NaN), g = num(p && p.g, NaN);
            if (!isFinite(t) || !isFinite(g) || g < 0) return [{ t: 0, g: 1 }];
            out.push({ t: t, g: g });
        }
        out.sort(function (a, b) { return a.t - b.t; });
        return out;
    }

    /* ─────────────────────────────── build ────────────────────────────────── */

    /**
     * spec = {
     *   duration,            секунды, полная длина исходника
     *   baseDb,              постоянное усиление (ползунок + нормализация)
     *   fadeInSec/fadeOutSec 0 = выключено
     *   regions: [{start, end, gain}]   gain в дБ
     *   restore: [{t, g}]    возврат громкости под затуханием записи
     *                        (FadeRestore.restorePoints); null = нет
     *   trimStart/trimEnd    рабочее окно; по умолчанию весь файл
     *   rampMs               по умолчанию DEFAULT_RAMP_MS
     * }
     */
    function build(spec) {
        spec = spec || {};
        var duration = Math.max(0, num(spec.duration, 0));
        var t0 = clamp(num(spec.trimStart, 0), 0, duration);
        var t1 = clamp(num(spec.trimEnd, duration), t0, duration);
        if (t1 - t0 <= EPS) t1 = t0 + Math.max(duration, 1e-3);

        var ramp = Math.max(0, num(spec.rampMs, DEFAULT_RAMP_MS) / 1000);
        var base = dbToLin(num(spec.baseDb, 0));

        var segs = resolveRegions(spec.regions, t0, t1, ramp);
        var rPts = regionPoints(segs);
        var fPts = fadePoints(t0, t1, spec.fadeInSec, spec.fadeOutSec);
        // Третий множитель — возврат громкости под затуханием, уже сведённым
        // в запись (`public/js/fade-restore.js` строит его точки). Он же
        // задаёт новую концовку: спуск к нулю и ноль после неё.
        var sPts = restorePointsOf(spec.restore);

        // Объединяем времена изломов всех множителей.
        var times = [], i;
        for (i = 0; i < rPts.length; i++) times.push(rPts[i].t);
        for (i = 0; i < fPts.length; i++) times.push(fPts[i].t);
        for (i = 0; i < sPts.length; i++) if (sPts[i].t > t0 && sPts[i].t < t1) times.push(sPts[i].t);
        times.push(t0, t1);
        times.sort(function (a, b) { return a - b; });

        // Дробим только там, где меняются хотя бы ДВА множителя: там
        // произведение квадратично, а контракт линеен. Везде остальном
        // (линейное × константы) линейной интерполяции достаточно ТОЧНО.
        var dense = [];
        for (i = 0; i < times.length; i++) {
            var ta = times[i];
            dense.push(ta);
            var tb = times[i + 1];
            if (tb == null || tb - ta <= EPS) continue;
            var varying = (variesOver(rPts, ta, tb) ? 1 : 0) + (variesOver(fPts, ta, tb) ? 1 : 0) + (variesOver(sPts, ta, tb) ? 1 : 0);
            if (varying >= 2) {
                for (var s = 1; s < QUAD_SUBDIV; s++) dense.push(ta + (tb - ta) * (s / QUAD_SUBDIV));
            }
        }

        var points = [];
        for (i = 0; i < dense.length; i++) {
            var t = dense[i];
            var g = base * interpAt(rPts, t) * interpAt(fPts, t) * interpAt(sPts, t);
            var prev = points.length ? points[points.length - 1] : null;
            // Схлопываем дубли по времени, но СОХРАНЯЕМ намеренную ступень
            // (две точки с одним t и разными g) — она бывает при rampMs=0.
            if (prev && Math.abs(prev.t - t) <= EPS && Math.abs(prev.g - g) <= EPS) continue;
            points.push({ t: t, g: g });
        }
        if (!points.length) points.push({ t: t0, g: base });

        return makeEnvelope(points, { duration: duration, trimStart: t0, trimEnd: t1, rampMs: ramp * 1000, baseDb: num(spec.baseDb, 0) });
    }

    /* ───────────────────────────── renderers ──────────────────────────────── */

    function makeEnvelope(points, meta) {
        var isConst = true;
        for (var i = 1; i < points.length; i++) {
            if (Math.abs(points[i].g - points[0].g) > 1e-7) { isConst = false; break; }
        }

        var env = {
            points: points,
            meta: meta,

            /** Контракт: линейная интерполяция, удержание за краями. */
            valueAt: function (t) { return interpAt(points, t); },

            /** Огибающая — константа? Тогда лейн вправе взять более дешёвую,
             *  но ДОКАЗАННО эквивалентную форму (`volume=XdB` вместо aeval).
             *  Эквивалентность держит вектор `constant_matches_plain_volume`. */
            isConstant: function () { return isConst; },
            constantGain: function () { return points[0].g; },
            constantDb: function () { return 20 * Math.log10(Math.max(points[0].g, 1e-12)); },
            isIdentity: function () { return isConst && Math.abs(points[0].g - 1) < 1e-7; },

            /** Максимум огибающей — по нему решается, нужен ли потолок. */
            peakGain: function () {
                var m = 0;
                for (var k = 0; k < points.length; k++) if (points[k].g > m) m = points[k].g;
                return m;
            },

            /**
             * Пик РЕЗУЛЬТАТА — max |x(t)·g(t)| по всем каналам, тем же курсором,
             * что applyToChannels, но без записи. По нему решается, нужен ли
             * потолок −1 dBFS: прежний признак «пик исходника × максимум
             * огибающей» — верхняя граница, и при возврате громкости под
             * затуханием (+48 дБ на тихом хвосте) он включал бы лимитер на
             * каждом файле, хотя громкий хвост выходит ниже потолка.
             */
            peakOn: function (channelArrays, sampleRate) {
                if (!channelArrays || !channelArrays.length) return 0;
                var n = channelArrays[0].length, nCh = channelArrays.length;
                var inv = 1 / sampleRate, idx = 0, last = points.length - 1, peak = 0;
                for (var i = 0; i < n; i++) {
                    var t = i * inv;
                    while (idx < last && points[idx + 1].t <= t) idx++;
                    var g;
                    if (t <= points[0].t) g = points[0].g;
                    else if (idx >= last) g = points[last].g;
                    else {
                        var a = points[idx], b = points[idx + 1], span = b.t - a.t;
                        g = span <= EPS ? b.g : a.g + (b.g - a.g) * ((t - a.t) / span);
                    }
                    if (g === 0) continue;
                    for (var c = 0; c < nCh; c++) {
                        var v = channelArrays[c][i] * g;
                        if (v < 0) v = -v;
                        if (v > peak) peak = v;
                    }
                }
                return peak;
            },

            /**
             * Превью (живой AudioContext) И lite-лейн (OfflineAudioContext) —
             * ОДИН И ТОТ ЖЕ вызов. Web Audio исполняет контракт нативно и
             * сэмплово-точно, в аудиопотоке: никакого requestAnimationFrame,
             * от которого зависела старая точность границы.
             *
             * @param when   момент аудио-часов, соответствующий offset
             * @param offset позиция внутри файла, с которой начинается звук
             */
            scheduleOn: function (param, when, offset) {
                when = num(when, 0);
                offset = num(offset, 0);
                try { param.cancelScheduledValues(when); } catch (_) {}
                param.setValueAtTime(interpAt(points, offset), when);
                for (var k = 0; k < points.length; k++) {
                    var rel = points[k].t - offset;
                    if (rel <= 0) continue;
                    var at = when + rel;
                    var prevT = k > 0 ? points[k - 1].t : null;
                    if (prevT !== null && Math.abs(points[k].t - prevT) <= EPS) param.setValueAtTime(points[k].g, at);
                    else param.linearRampToValueAtTime(points[k].g, at);
                }
                return param;
            },

            /**
             * Стриминг-лейн: применить огибающую к одному чанку.
             * Курсор по точкам, а не бинарный поиск на каждый сэмпл — иначе
             * пятичасовой файл платит O(N log P) вместо O(N).
             */
            applyToChannels: function (channelArrays, startSample, sampleRate) {
                if (!channelArrays || !channelArrays.length) return channelArrays;
                if (env.isIdentity()) return channelArrays;
                var n = channelArrays[0].length, nCh = channelArrays.length;
                var inv = 1 / sampleRate;
                var idx = 0, last = points.length - 1;
                for (var i = 0; i < n; i++) {
                    var t = (startSample + i) * inv;
                    while (idx < last && points[idx + 1].t <= t) idx++;
                    var g;
                    if (t <= points[0].t) g = points[0].g;
                    else if (idx >= last) g = points[last].g;
                    else {
                        var a = points[idx], b = points[idx + 1], span = b.t - a.t;
                        g = span <= EPS ? b.g : a.g + (b.g - a.g) * ((t - a.t) / span);
                    }
                    if (g === 1) continue;
                    for (var c = 0; c < nCh; c++) channelArrays[c][i] *= g;
                }
                return channelArrays;
            },

            /**
             * FFmpeg-лейн (WASM и сервер).
             *
             * Кусочно-линейная функция — СБАЛАНСИРОВАННЫМ деревом `if(lt(t,…))`:
             * поиск отрезка двоичный, лист — линейная формула своего отрезка,
             * снаружи — удержание крайних значений:
             *
             *   if(lt(t,t0), g0, if(gte(t,tN), gN, <дерево отрезков>))
             *
             * `if` в выражениях FFmpeg ленивый — считается только выбранная
             * ветка, поэтому сэмпл стоит log₂N сравнений и одну формулу.
             *
             * До 2026-10-20 здесь была плоская сумма зажатых рамп
             * (g0 + Σ Δg·clip((t−tᵢ)/Δt)) — на КАЖДЫЙ сэмпл считались ВСЕ
             * слагаемые. Замер (ffmpeg 7.0, 240 с стерео, нативно; WASM в разы
             * медленнее):
             *
             *   отрезков   сумма      дерево
             *     10       28.9 с     5.7 с     (выход совпал до 7.6e-6)
             *     50      228.7 с    10.9 с
             *    200     1288.5 с    10.2 с
             *
             * Глубина дерева — log₂N (10 при MAX_POINTS = 1024), парсер её
             * держит. Формат обязан совпадать с PHP-близнецом символ в символ.
             */
            toFFmpegExpr: function () {
                var n = points.length;
                if (n < 2 || isConst) return ffNum(points[0].g);
                function leaf(k) {
                    var a = points[k], b = points[k + 1];
                    var d = b.g - a.g, span = b.t - a.t;
                    if (Math.abs(d) < NUM_EPS) return ffNum(a.g);
                    if (span < NUM_EPS) return ffNum(b.g);
                    return '(' + ffNum(a.g) + '+' + ffNum(d) + '*(t-' + ffNum(a.t) + ')/' + ffNum(span) + ')';
                }
                function tree(lo, hi) {
                    if (hi - lo === 1) return leaf(lo);
                    var mid = (lo + hi) >> 1;
                    return 'if(lt(t,' + ffNum(points[mid].t) + '),' + tree(lo, mid) + ',' + tree(mid, hi) + ')';
                }
                return 'if(lt(t,' + ffNum(points[0].t) + '),' + ffNum(points[0].g)
                    + ',if(gte(t,' + ffNum(points[n - 1].t) + '),' + ffNum(points[n - 1].g)
                    + ',' + tree(0, n - 1) + '))';
            }
        };
        return env;
    }

    /* ───────────────────────── FFmpeg filter assembly ─────────────────────── */

    var FF_LAYOUTS = { 1: 'mono', 2: 'stereo', 3: '3.0', 4: 'quad', 5: '5.0', 6: '5.1', 7: '6.1', 8: '7.1' };

    /**
     * Готовая FFmpeg-строка фильтра для огибающей.
     *
     * Здесь же — обе мины `aeval`, найденные замером 2026-08-21. Обе тихие,
     * поэтому обе закрыты в ОДНОМ месте, а не в каждом вызывающем:
     *
     *  1. `aeval` с ОДНИМ выражением отдаёт ОДИН канал. Стерео молча
     *     схлопывается в моно, без единой ошибки в логе. Поэтому выражение
     *     дублируется на каждый канал.
     *  2. `aeval` выставляет раскладку каналов «unknown», и AAC-энкодер такой
     *     вход ОТВЕРГАЕТ: `Unsupported channel layout "2 channels"` — то есть
     *     экспорт в m4a (второй по популярности формат этого лейна) просто
     *     падал бы. Опция `channel_layout=` у самого `aeval` НЕ помогает
     *     (проверено) — помогает только отдельный `aformat` следом.
     */
    function toFFmpegFilter(env, nChannels) {
        var ch = Math.max(1, Math.min(8, Math.round(num(nChannels, 2))));
        var expr = env.toFFmpegExpr();
        var parts = [];
        for (var c = 0; c < ch; c++) parts.push('val(' + c + ')*(' + expr + ')');
        var layout = FF_LAYOUTS[ch] || (ch + 'c');
        return "aeval=exprs='" + parts.join('|').replace(/,/g, '\\,') + "'"
            + ',aformat=channel_layouts=' + layout;
    }

    /* ───────────────────────── потолок −1 dBFS ────────────────────────────── */

    /** Время атаки `alimiter` по умолчанию, мс. Ровно на столько он ЗАДЕРЖИВАЕТ
     *  сигнал: это его lookahead. */
    var CEILING_ATTACK_MS = 5;

    /**
     * Фильтр-потолок для FFmpeg — с компенсацией задержки лимитера.
     *
     * Живёт здесь, а не в вызывающем, потому что решение «нужен ли потолок»
     * принимается по `peakGain()` этой же огибающей: два ответа на один вопрос
     * снова разъехались бы.
     *
     * ⚠️ Замер 2026-08-21, импульс в начале и в самом конце трёхсекундного файла:
     *
     *   голый alimiter        длина 144000, импульсы [48239]        ← хвост ПОТЕРЯН
     *   + atrim               длина 143761, импульсы [48000]        ← хвост ПОТЕРЯН
     *   + apad + atrim        длина 144000, импульсы [48000,143950] ← верно
     *
     * То есть `alimiter` не просто сдвигает всё на своё время атаки (5 мс =
     * 239 сэмплов на 48 кГц) — он ещё и НЕ удлиняет выход, поэтому последние
     * 5 мс записи молча пропадают. `apad` даёт лимитеру доработать до конца,
     * `atrim` снимает вносимую им задержку с начала.
     *
     * Задержка была и до 21.08.2026 — просто её нечем было заметить, пока
     * лейны не начали сравниваться друг с другом. Тот же голый `alimiter=`
     * стоит ещё в полутора десятках мест проекта.
     */
    /**
     * На сколько СЭМПЛОВ `alimiter` задерживает сигнал.
     *
     * Замерено 2026-08-21 на ffmpeg 7.0, 12 комбинаций (частоты 22050/44100/
     * 48000/96000 × атака 3/5/10 мс): задержка = floor(attack·sr/1000) − 1,
     * без единого исключения, включая нецелые случаи (44100×5 мс = 220.5 →
     * 219).
     */
    function ceilingDelaySamples(sampleRate) {
        return Math.max(0, Math.floor(CEILING_ATTACK_MS * num(sampleRate, 48000) / 1000) - 1);
    }

    function ceilingFilter(limitLin, sampleRate) {
        var d = ceilingDelaySamples(sampleRate);
        // Паддинг С ЗАПАСОМ, а не ровно по формуле: WASM-сборка ffmpeg у нас
        // другой версии, чем та, на которой формула замерена. Если там
        // задержка окажется чуть больше, запас поглотит разницу — несколько
        // лишних сэмплов тишины в конце безвредны, а потерянный хвост нет.
        // Асимметрия намеренная: ошибаться надо в сторону сохранности.
        var pad = d + 8;
        return 'apad=pad_len=' + pad
            + ',alimiter=limit=' + limitLin + ':level=disabled'
            + ',atrim=start_sample=' + d + ',asetpts=PTS-STARTPTS';
    }

    var api = {
        DEFAULT_RAMP_MS: DEFAULT_RAMP_MS,
        CEILING_ATTACK_MS: CEILING_ATTACK_MS,
        ceilingFilter: ceilingFilter,
        ceilingDelaySamples: ceilingDelaySamples,
        build: build,
        fromPoints: function (points, meta) {
            var p = (points || []).map(function (x) { return { t: num(x.t, 0), g: num(x.g, 1) }; });
            p.sort(function (a, b) { return a.t - b.t; });
            if (!p.length) p = [{ t: 0, g: 1 }];
            return makeEnvelope(p, meta || {});
        },
        toFFmpegFilter: toFFmpegFilter,
        dbToLin: dbToLin,
        _internals: { resolveRegions: resolveRegions, regionPoints: regionPoints, fadePoints: fadePoints, interpAt: interpAt }
    };

    if (root) root.GainEnvelope = api;
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : (typeof globalThis !== 'undefined' ? globalThis : null));
